import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { Reveal, MaskedLineInView } from "../components/Reveal";
import { ARCHES_IMG } from "../data/products";

const HadithCard = ({ h, i, lang }) => (
  <Reveal delay={0.08 * i} className="h-full">
    <div className="h-full border border-gold/25 rounded-2xl p-7 bg-onyx/60 flex flex-col items-center text-center gap-3" data-testid={"hadith-card-" + (i + 1)}>
      <p className="font-arabic text-2xl md:text-3xl text-gold leading-loose" dir="rtl" data-testid={"hadith-arabic-" + (i + 1)}>{h.ar}</p>
      {lang !== "ar" && (
        <p className="text-ivory/85 text-sm italic leading-relaxed" data-testid={"hadith-translation-" + (i + 1)}>« {h.tr} »</p>
      )}
      <span className="mt-auto text-[0.62rem] tracking-[0.2em] uppercase text-gold/80 border border-gold/30 rounded-full px-4 py-1.5" data-testid={"hadith-ref-" + (i + 1)}>{h.ref}</span>
    </div>
  </Reveal>
);

export default function Story() {
  const { L, lang } = useStore();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const hadiths =
    L === undefined
      ? []
      : [
          { ar: "أَطْيَبُ الطِّيبِ الْمِسْكُ", tr: L.story.h1, ref: "Jamiʿ at-Tirmidhi — 991" },
          { ar: "مِنْ خَيْرِ طِيبِكُمْ الْمِسْكُ", tr: L.story.h2, ref: "Sunan an-Nasa'i — 1906" },
          { ar: "الطُّهُورُ شَطْرُ الْإِيمَانِ", tr: L.story.h3, ref: "Sahih Muslim — 223" },
        ];

  return (
    <div className="pt-32 pb-24 pattern-story min-h-screen" data-testid="story-page">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><p className="eyebrow">{L.story.eyebrow}</p></Reveal>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory mt-4" data-testid="story-title">
            <MaskedLineInView>{L.story.title.split("\n")[0]}</MaskedLineInView>
            <MaskedLineInView delay={0.12}><span className="text-gold">{L.story.title.split("\n")[1]}</span></MaskedLineInView>
          </h1>
          <Reveal delay={0.2}>
            <p className="text-sand mt-6 text-sm lg:text-base leading-relaxed" data-testid="story-lead">{L.story.lead}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-16">
          <div ref={ref} className="relative overflow-hidden rounded-2xl border border-gold/20">
            <motion.img style={{ y }} src={ARCHES_IMG} alt="Arches arabes" className="w-full h-[300px] lg:h-[480px] object-cover" data-testid="story-image" />
            <div className="absolute inset-0" style={{ boxShadow: "inset 0 -90px 110px -60px rgba(12,10,9,0.92)" }} />
          </div>
        </Reveal>

        {/* HADITH */}
        <section className="mt-24" data-testid="story-hadith-section">
          <Reveal><h2 className="font-display text-3xl lg:text-4xl text-ivory text-center">{L.story.hadithTitle}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {hadiths.map((h, i) => (
              <HadithCard key={i} h={h} i={i} lang={lang} />
            ))}
          </div>
        </section>

        {/* HISTORY OF MUSK */}
        <section className="mt-24" data-testid="story-history-section">
          <Reveal><h2 className="font-display text-3xl lg:text-4xl text-ivory text-center">{L.story.historyTitle}</h2></Reveal>
          <div className="mt-14 max-w-3xl mx-auto relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-gold/50 via-gold/25 to-transparent" />
            {L.story.timeline.map((t, i) => (
              <Reveal key={i} delay={0.06 * i}>
                <div className={"relative flex " + (i % 2 === 0 ? "md:justify-start" : "md:justify-end")}>
                  <div className={"ml-12 md:ml-0 md:w-[46%] border border-gold/20 rounded-2xl p-6 bg-onyx/60 hover:border-gold/45 transition-colors duration-500 " + (i % 2 === 0 ? "md:mr-auto" : "md:ml-auto")} data-testid={"timeline-item-" + i}>
                    <span className="absolute left-4 md:left-1/2 -translate-x-1/2 mt-1.5 w-2.5 h-2.5 rounded-full bg-gold shadow-[0_0_16px_rgba(212,175,55,0.7)]" style={{ position: "absolute" }} />
                    <h3 className="font-display text-xl text-gold">{t.t}</h3>
                    <p className="text-ivory/80 text-sm leading-relaxed mt-2">{t.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <div className="flex flex-wrap justify-center gap-4 mt-20">
          <Link to="/mission" data-testid="story-cta-mission" className="btn-gold rounded-full px-8 py-3.5 text-[0.7rem] tracking-[0.25em] uppercase font-semibold">{L.story.ctaMission}</Link>
          <Link to="/shop" data-testid="story-cta-shop" className="btn-ghost inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-[0.7rem] tracking-[0.25em] uppercase">{L.story.ctaShop} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </div>
  );
}
